import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Trash2, Film, Tv, Calendar, Star } from 'lucide-react';
import { FavoriteItem } from '../types';
import { useFavorites } from '../hooks/useFavorites';
import { getImageUrl } from '../api/tmdb';

const Favorites: React.FC = () => {
  const { favorites, removeFromFavorites, clearFavorites, getFavoritesByType } = useFavorites();
  const [activeTab, setActiveTab] = useState<'all' | 'movie' | 'tv'>('all');

  // Get filtered favorites based on active tab
  const getFilteredFavorites = (): FavoriteItem[] => {
    if (activeTab === 'all') return favorites;
    return getFavoritesByType(activeTab);
  };

  const filteredFavorites = getFilteredFavorites();

  const handleRemoveFavorite = (id: number, type: 'movie' | 'tv') => {
    removeFromFavorites(id, type);
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to remove all favorites?')) {
      clearFavorites();
    }
  };

  // Tab component
  const Tab = ({ 
    id, 
    label, 
    count, 
    icon: Icon 
  }: { 
    id: 'all' | 'movie' | 'tv'; 
    label: string; 
    count: number; 
    icon: React.ComponentType<{ className?: string }>; 
  }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors duration-200 ${
        activeTab === id
          ? 'bg-primary-600 text-white'
          : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
      }`}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
      <span className="text-xs bg-white/20 dark:bg-black/20 px-2 py-1 rounded-full">
        {count}
      </span>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-2"
      >
        <div className="flex items-center justify-center space-x-2 mb-2">
          <Heart className="h-7 w-7 text-red-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Favorites</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Your saved movies and TV series
        </p>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center space-x-1 mb-2"
      >
        <Tab
          id="all"
          label="All"
          count={favorites.length}
          icon={Heart}
        />
        <Tab
          id="movie"
          label="Movies"
          count={getFavoritesByType('movie').length}
          icon={Film}
        />
        <Tab
          id="tv"
          label="TV Series"
          count={getFavoritesByType('tv').length}
          icon={Tv}
        />
      </motion.div>

      {/* Clear All Button */}
      {favorites.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center mb-2"
        >
          <button
            onClick={handleClearAll}
            className="flex items-center space-x-2 px-3 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
          >
            <Trash2 className="h-4 w-4" />
            <span>Clear All</span>
          </button>
        </motion.div>
      )}

      {/* Empty State */}
      {filteredFavorites.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-8"
        >
          <div className="bg-dark-800/70 dark:bg-dark-700/70 rounded-xl p-6 flex flex-col items-center shadow-md max-w-xs w-full">
            <Heart className="h-10 w-10 text-gray-400 mb-2" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
              {activeTab === 'all' 
                ? 'No favorites yet' 
                : `No ${activeTab === 'movie' ? 'movies' : 'TV series'} in favorites`
              }
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center mb-4">
              {activeTab === 'all' 
                ? 'Start adding movies and TV series to your favorites to see them here.'
                : `Start adding ${activeTab === 'movie' ? 'movies' : 'TV series'} to your favorites.`
              }
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm"
            >
              Explore Content
            </button>
          </div>
        </motion.div>
      )}

      {/* Favorites Grid */}
      {filteredFavorites.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
        >
          <AnimatePresence>
            {filteredFavorites.map((item) => (
              <motion.div
                key={`${item.type}-${item.id}`}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="group relative bg-white dark:bg-dark-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                {/* Poster */}
                <div className="relative aspect-[2/3] overflow-hidden">
                  <img
                    src={getImageUrl(item.poster_path)}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {/* Remove Button */}
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleRemoveFavorite(item.id, item.type)}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                    aria-label="Remove from favorites"
                  >
                    <Trash2 className="h-4 w-4" />
                  </motion.button>
                  {/* Type Badge */}
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary-600 text-white text-xs font-semibold rounded-md">
                    {item.type === 'movie' ? 'Movie' : 'TV Series'}
                  </div>
                  {/* Rating Badge */}
                  <div className="absolute bottom-2 left-2 flex items-center space-x-1 px-2 py-0.5 bg-black/70 text-white text-xs rounded-md">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    <span>{item.vote_average.toFixed(1)}</span>
                  </div>
                </div>
                {/* Content */}
                <div className="p-2">
                  {/* Title */}
                  <h3 className="font-semibold text-gray-900 dark:text-white text-xs line-clamp-2 mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200">
                    {item.title}
                  </h3>
                  {/* Added Date */}
                  <div className="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-400">
                    <Calendar className="h-3 w-3" />
                    <span>Added {new Date(item.addedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
};

export default Favorites; 